/**
 * The outline down the left of settings.
 *
 * Seven peer tabs in a wrapping row asked you to read all seven before you
 * could pick one. Four named groups means you read four, then one. The group
 * headings are set as quiet foreground text rather than the tracked-out
 * all-caps labels this pattern usually reaches for: they are signposts you
 * read past, not things to look at.
 */
import { NavLink } from 'react-router-dom';
import { sectionsInGroup, settingsPath, visibleGroups } from './settingsSections';

interface SettingsRailProps {
  schoolSlug: string;
  /** Areas still needing setup, so the rail can mark them the way the overview does. */
  needsAttention: ReadonlySet<string>;
  isAdmin: boolean;
}

export function SettingsRail({ schoolSlug, needsAttention, isAdmin }: SettingsRailProps) {
  return (
    <nav aria-label="Settings sections" className="w-56 shrink-0">
      <div className="sticky top-6 space-y-6">
        <NavLink
          to={settingsPath(schoolSlug)}
          end
          className={({ isActive }) =>
            `block rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              isActive ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`
          }
        >
          Overview
        </NavLink>

        {visibleGroups(isAdmin).map((group) => (
          <div key={group.key}>
            {group.label && (
              <h2 className="px-3 text-sm font-medium text-foreground">{group.label}</h2>
            )}
            <ul className={group.label ? 'mt-1.5 space-y-0.5' : 'space-y-0.5'}>
              {sectionsInGroup(group.key, isAdmin).map((section) => (
                <li key={section.slug}>
                  <NavLink
                    to={settingsPath(schoolSlug, section.slug)}
                    className={({ isActive }) =>
                      `flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors ${
                        isActive
                          ? 'bg-muted font-medium text-foreground'
                          : 'text-muted-foreground hover:text-foreground'
                      }`
                    }
                  >
                    <span className="truncate">{section.label}</span>
                    {needsAttention.has(section.slug) && (
                      <span
                        className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-[--settings-attention]"
                        aria-label="Needs setting up"
                      />
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );
}

export default SettingsRail;
